import { gql, useMutation } from '@apollo/client';
import React from 'react';
import { useToast } from '@chakra-ui/react';
import { Button } from 'web2/app/components/button';
import { CommentItemCoreFragment, CommentItemFragment } from 'shared/__generated__/graphql';
import BasicEditor from 'web2/app/components/by-type/rich-text/basic/basic-editor';
import { useAuth } from 'shared/misc/hooks/useAuth';
import LikeStars from 'web2/app/components/by-type/likes/like-stars';
import useMutationWithAutoToast from 'shared/misc/hooks/useMutationWithAutoToast';
import { getRelativeDateString } from 'shared/dates';
import { safeSendWebViewMessage } from 'shared/webview';
import { GlobalWebViewMessages } from 'shared/webview/messages';
import {
  ENDORSE_COMMENT_MUTATION,
  UNENDORSE_COMMENT_MUTATION,
} from 'shared/misc/graphql/CommentMutations';
import PerfImageFromFile from 'web2/app/components/by-type/images/perf-image-from-file';
import CommentComposer from 'web2/app/components/by-type/comments/comment-composer';
import { useThemeColors } from 'shared/misc/hooks/useThemeColors';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'web2/app/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'web2/app/components/ui/tooltip';
import { TWLink } from 'web2/app/components/link';
import { useState } from 'react';

gql(/* GraphQL */ `
  fragment CommentItemCore on Comment {
    id
    createdAt
    content
    contentHTML
    contentLexical
    isEndorsed
    endorsementCount
    targetRecId
    parentCommentId
    isHidden
    originUser {
      ...UserCore
    }
  }
`);

gql(/* GraphQL */ `
  fragment CommentItem on Comment {
    ...CommentItemCore
  }
`);

gql(/* GraphQL */ `
  fragment CommentItemWithReplies on Comment {
    ...CommentItemCore
    replies {
      ...CommentItemCore
    }
  }
`);

type Props = {
  comment: CommentItemCoreFragment;
  onReply?: (replyToComment: CommentItemCoreFragment) => void;
};

function CommentItem({ comment, ...props }: Props) {
  const auth = useAuth();
  const themeColors = useThemeColors();
  const [isEditing, setIsEditing] = useState(false);

  const [deleteCommentFromRec] = useMutation(DELETE_COMMENT_FROM_REC);

  const toast = useToast();

  const deleteComment = async () => {
    await deleteCommentFromRec({
      variables: { targetCommentId: comment.id, targetRecId: comment.targetRecId },
      refetchQueries: ['getRecView', 'getCommentsQuery'],
      update(cache) {
        const normalizedId = cache.identify({ id: comment.id, __typename: 'Comment' });
        cache.evict({ id: normalizedId });
        cache.gc();
      },
      onCompleted: async () => {
        toast({
          title: 'Comment deleted.',
          status: 'success',
          duration: 2000,
          isClosable: true,
          position: 'top',
        });
        try {
          safeSendWebViewMessage(GlobalWebViewMessages.HAPTIC_SUCCESS);
        } catch (e) {}
      },
      onError: () => {
        toast({
          title: 'An error occured...',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top',
        });
      },
    });
  };

  const [endorseComment] = useMutationWithAutoToast(ENDORSE_COMMENT_MUTATION, {
    variables: { targetCommentId: comment.id },
    optimisticResponse: {
      endorseComment: {
        __typename: 'Comment',
        id: comment.id,
        isEndorsed: true,
        endorsementCount: (comment.endorsementCount ?? 0) + 1,
      },
    },
  });

  const [unendorseComment] = useMutationWithAutoToast(UNENDORSE_COMMENT_MUTATION, {
    variables: { targetCommentId: comment.id },
    optimisticResponse: {
      unendorseComment: {
        __typename: 'Comment',
        id: comment.id,
        isEndorsed: false,
        endorsementCount: (comment.endorsementCount ?? 0) - 1,
      },
    },
  });

  const avatarSize = 30;

  const [replyingTo, setIsReplyingTo] = React.useState<CommentItemCoreFragment | null>(null);

  const onReply = (replyToComment: CommentItemCoreFragment = comment) => {
    if (props.onReply) {
      props.onReply(replyToComment);
      return;
    }
    setIsReplyingTo(replyToComment);
  };

  if (comment.isHidden) {
    return null;
  }

  return (
    <div
      className={`flex flex-col gap-[12px] w-full p-[16px] ${
        comment.parentCommentId && false
          ? 'border-t border-t-dashed border-t-brand-main'
          : 'border border-dashed border-brand-main'
      }`}
    >
      <div className="flex flex-row gap-[12px]">
        <div className="self-start h-full grow">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <TWLink to={`/u/${comment.originUser?.username}`}>
                  {comment.originUser?.avatarPhotoFile ? (
                    <PerfImageFromFile
                      file={comment.originUser?.avatarPhotoFile}
                      useFallbackAvatarSrc
                      alt="image"
                      quality={10}
                      width={avatarSize}
                      height={avatarSize}
                      style={{
                        width: `${avatarSize}px`,
                        height: `${avatarSize}px`,
                        minWidth: `${avatarSize}px`,
                        minHeight: `${avatarSize}px`,
                      }}
                    />
                  ) : (
                    <PerfImageFromFile
                      useFallbackAvatarSrc
                      alt="image"
                      quality={10}
                      width={avatarSize}
                      height={avatarSize}
                      style={{
                        minWidth: `${avatarSize}px`,
                        minHeight: `${avatarSize}px`,
                      }}
                    />
                  )}
                </TWLink>
              </TooltipTrigger>
              <TooltipContent>
                <div className="flex flex-row p-[6px] gap-[6px]">
                  <TWLink
                    to={`/u/${comment.originUser?.username}`}
                    className="max-w-[150px] truncate text-brand-main hover:text-brand-highlight !overflow-visible"
                  >
                    @{comment.originUser?.username}
                  </TWLink>
                  <div>/</div>
                  <div>
                    {comment.originUser?.firstName} {comment.originUser?.lastName}
                  </div>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <div className="flex flex-col gap-[12px] relative top-[-1px] w-full typography-body text-brand-main">
          <div className="flex justify-between w-full">
            <div className="flex flex-row">
              <TWLink
                to={`/u/${comment.originUser?.username}`}
                className="typography-action-sm max-w-[150px] truncate text-brand-main hover:text-brand-highlight !overflow-visible"
              >
                @{comment.originUser?.username}
              </TWLink>
            </div>
            {auth.user && auth.user.id === comment.originUser?.id && !isEditing && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    aria-label="Options"
                    className="typography-action-sm w-fit cursor-pointer border-none p-0 bg-transparent"
                  >
                    ⋮
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-fit">
                  <DropdownMenuItem onClick={() => deleteComment()}>Delete</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setIsEditing(true)}>Edit</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
          {isEditing ? (
            <CommentComposer
              targetRecId={comment.targetRecId as string}
              onCancel={() => setIsEditing(false)}
              onComplete={() => setIsEditing(false)}
              editMode
              withBorder={false}
              comment={comment}
            />
          ) : typeof window !== 'undefined' ? (
            <BasicEditor
              key={comment.createdAt}
              isReadOnly={true}
              initialEditorState={comment.contentLexical}
              maxLength={30}
              noOfLines={10}
              contentEditableProps={{ className: 'text-[18px] typography-brand-editor text-brand-main' }}
            />
          ) : (
            <span className="!typography-editor editor-paragraph font-weight-normal font-size-[16px]">
              {comment.content}
            </span>
          )}
          {!isEditing && (
            <div className="flex justify-between items-center w-full">
              <div className="typography-action-sm text-[11px] sm:text-[13.5px] opacity-80">
                {getRelativeDateString(new Date(comment.createdAt))}
              </div>
              <div className="flex flex-row items-center typography-action-sm h-[12px] gap-[6px] relative">
                <Button
                  variant="linkNoBorder"
                  className="text-brand-action-sm text-opacity-80 text-[11px] sm:text-[13.5px]"
                  onClick={() => onReply(comment)}
                >
                  REPLY
                </Button>
                <LikeStars
                  size={25}
                  isEndorsed={!!comment.isEndorsed}
                  endorsementCount={comment.endorsementCount ?? 0}
                  unendorse={unendorseComment}
                  endorse={endorseComment}
                  targetCommentId={comment.id}
                  ownerId={comment.originUser?.id || ''}
                  withText={false}
                />
                {/* <Button variant="textOnly">REPLY</Button> */}
              </div>
            </div>
          )}
        </div>
      </div>
      {'replies' in comment && Array.isArray(comment.replies) && comment.replies.length > 0 ? (
        <div className="flex flex-col gap-[12px] mt-[12px]">
          {comment.replies.map((replyComment) => (
            <CommentItem key={replyComment.id} comment={replyComment} onReply={onReply} />
          ))}
        </div>
      ) : null}
      {replyingTo ? (
        <CommentComposer
          replyToComment={replyingTo}
          targetRecId={comment.targetRecId as string}
          parentCommentId={comment.id}
          onCancel={() => setIsReplyingTo(null)}
          onComplete={() => setIsReplyingTo(null)}
        />
      ) : null}
    </div>
  );
}

const DELETE_COMMENT_FROM_REC = gql(/* GraphQL */ `
  mutation deleteCommentFromRec($targetCommentId: String!, $targetRecId: String!) {
    deleteCommentFromRec(targetCommentId: $targetCommentId, targetRecId: $targetRecId) {
      id
      # ...RecommendationItemComments
    }
  }
`);

export default CommentItem;
